import { Typography } from '@material-ui/core';
import { InfoWindow, Marker, useGoogleMap } from '@react-google-maps/api';
import React, { useEffect, useState } from 'react';
import { DomesticTsunami, JMAQuake } from './useEQ';
import MBox from '@material-ui/core/Box';
import { isBefore, parse, subMinutes } from 'date-fns';
import { ja } from 'date-fns/locale';
import { utcToZonedTime } from 'date-fns-tz';

interface Props {
  data: JMAQuake;
}

export const RED_EQ = '#FF0000';
export const YELLOW_EQ = '#FFFF00';
export const GREEN_EQ = '#00FF00';

export const EQMarker = ({ data }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const map = useGoogleMap();

  const { hypocenter, domesticTsunami, maxScale, time } = data.earthquake;

  const label: google.maps.MarkerLabel = {
    text: (maxScale / 10).toString(),
    color: 'white',
    fontSize: '1.5rem',
    fontWeight: 'bold',
  };

  const position: google.maps.LatLngLiteral = {
    lat: hypocenter.latitude,
    lng: hypocenter.longitude,
  };

  let tsunamiLabel = DomesticTsunami.None;
  switch (domesticTsunami) {
    case 'Checking':
      tsunamiLabel = DomesticTsunami.Checking;
      break;

    case 'NonEffective':
      tsunamiLabel = DomesticTsunami.NonEffective;
      break;

    case 'Unknown':
      tsunamiLabel = DomesticTsunami.Unknown;
      break;

    case 'Warning':
      tsunamiLabel = DomesticTsunami.Warning;
      break;

    case 'Watch':
      tsunamiLabel = DomesticTsunami.Watch;
      break;

    default:
      break;
  }

  useEffect(() => {
    if (map && isOpen) {
      let dragListener: google.maps.MapsEventListener | undefined;
      let clickListener: google.maps.MapsEventListener | undefined;

      const removeListeners = () => {
        dragListener?.remove();
        clickListener?.remove();
      };

      dragListener = map.addListener('drag', () => setIsOpen(false));
      clickListener = map.addListener('click', () => setIsOpen(false));

      return () => removeListeners();
    }
  }, [map, isOpen]);

  const parsedTime = parse(time, 'yyyy/MM/dd HH:mm:ss', new Date(), {
    locale: ja,
  });

  const nowJST = utcToZonedTime(new Date(), 'Asia/Tokyo');

  const isBefore10Minute = isBefore(subMinutes(nowJST, 10), parsedTime);
  const isBefore30Minute = isBefore(subMinutes(nowJST, 30), parsedTime);

  const markerColor = isBefore10Minute
    ? RED_EQ
    : isBefore30Minute
    ? YELLOW_EQ
    : GREEN_EQ;

  return hypocenter.latitude !== -200 && hypocenter.longitude !== -200 ? (
    <Marker
      onClick={() => setIsOpen((v) => !v)}
      label={label}
      icon={{
        path: google.maps.SymbolPath.CIRCLE,
        scale: 30,
        strokeColor: markerColor,
        strokeOpacity: 0.3,
        strokeWeight: 2,
        fillColor: markerColor,
        fillOpacity: 0.3,
      }}
      position={position}
    >
      {isOpen ? (
        <InfoWindow onCloseClick={() => setIsOpen(false)}>
          <MBox height="100%" width="100%">
            <Typography variant="body1">震源情報</Typography>
            <MBox paddingTop={1} display="grid" gridTemplateColumns="1fr 1fr">
              <Typography variant="body1">{hypocenter.name}</Typography>
              <Typography variant="body1">{time}</Typography>
            </MBox>

            <MBox
              paddingTop={1}
              paddingBottom={1}
              display="grid"
              gridTemplateColumns="1fr 1fr"
            >
              <Typography variant="body2">
                緯度: {hypocenter.latitude}
              </Typography>

              <Typography variant="body2">
                経度: {hypocenter.longitude}
              </Typography>
            </MBox>
            <MBox display="grid" gridTemplateColumns="1fr 1fr">
              <Typography variant="body2">
                深さ:{' '}
                {hypocenter.depth > -1
                  ? hypocenter.depth + 'km'
                  : 'データがない'}
              </Typography>
              <Typography variant="body2">
                マグニチュード:{' '}
                {hypocenter.magnitude > -1
                  ? hypocenter.magnitude
                  : 'データがない'}
              </Typography>
            </MBox>
            <Typography variant="body2">
              最大震度: {maxScale > -1 ? maxScale / 10 + '度' : 'データがない'}
            </Typography>
            <MBox paddingTop={1}>
              <Typography variant="body2">
                国内への津波: {tsunamiLabel}
              </Typography>
            </MBox>
          </MBox>
        </InfoWindow>
      ) : null}
    </Marker>
  ) : null;
};
